import { XClose } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import { getFormattedNumber } from 'src/utils/CommonFunctions';

interface IProps {
    label: string;
    minName: string;
    maxName: string;
    minPlaceholder: string;
    maxPlaceholder: string;
    params: any;
    onFilterChange: (field: string, value: any) => void;
    prefix?: string;
    isDisabled?: boolean;
}

/**
 * Min/Max range filter chip — ported from wal-frontend-v2's Finance RangePopover so the
 * Document Hub "Amount" filter looks and behaves exactly like WAL's Receivables one.
 * Values apply as the user types (debounced with the same 700ms delay as the search box).
 * Blur, click-outside and scroll only flush a debounce that is still pending — once the typed
 * value has been applied they cost nothing. A max lower than min is never sent to the API —
 * it is held back while typing and dropped when the field is left.
 */
const RangePopover = ({ label, minName, maxName, minPlaceholder, maxPlaceholder, params, onFilterChange, prefix, isDisabled }: IProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [minVal, setMinVal] = useState(params?.[minName] || '');
    const [maxVal, setMaxVal] = useState(params?.[maxName] || '');

    // Keep the latest onFilterChange in a ref so the debounced callback (created once) never
    // calls a stale parent handler.
    const onFilterChangeRef = useRef(onFilterChange);
    onFilterChangeRef.current = onFilterChange;

    const paramsRef = useRef(params);
    paramsRef.current = params;

    // Every filter push rebuilds the parent's params object, which refetches the list. Now that the
    // range applies while typing, blur/click-outside would re-push the value the debounce already
    // applied — so skip pushes that cannot change the applied filter.
    const pushValue = useCallback((name: string, value: string) => {
        if ((paramsRef.current?.[name] || '') === (value || '')) return;

        onFilterChangeRef.current(name, value || '');
    }, []);

    // Applies the typed range while the user is still typing, 700ms after they stop — same
    // debounce as the Document Hub search input. An incomplete/invalid range (max below min)
    // is not pushed to the API; the input keeps the typed value and shows the error state.
    const applyDebounce = useCallback(
        debounce((min: string, max: string) => {
            const isInvalid = !!(min && max && parseFloat(max) < parseFloat(min));

            pushValue(minName, min);
            if (!isInvalid) pushValue(maxName, max);
        }, 700),
        []
    );

    useEffect(() => () => applyDebounce.cancel(), []);

    // Sync local state with params when they change externally (e.g. filter reset).
    // Skipped while the popover is open so a debounced apply cannot clobber in-progress typing.
    useEffect(() => {
        if (isOpen) return;
        setMinVal(params?.[minName] || '');
        setMaxVal(params?.[maxName] || '');
    }, [params?.[minName], params?.[maxName], isOpen]);

    // Applies a debounce that is still pending — when the user blurs or closes within the 700ms
    // window — and drops a max that sits below the min. flush() is a no-op once the typed value has
    // already been applied, so leaving the field never fires a duplicate request.
    const commitPending = () => {
        applyDebounce.flush();

        if (minVal && maxVal && parseFloat(maxVal) < parseFloat(minVal)) {
            setMaxVal('');
            pushValue(maxName, '');
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                commitPending();
            }
        };

        const handleScroll = () => {
            setIsOpen(false);
            commitPending();
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen, minVal, maxVal]);

    const hasValue = minVal || maxVal;
    const isMaxBelowMin = !!(maxVal && minVal && parseFloat(maxVal) < parseFloat(minVal));

    const formatVal = (val: string) => `${prefix || ''}${getFormattedNumber(parseFloat(val), true, false)}`;
    const summary = minVal && maxVal ? `btw ${formatVal(minVal)} - ${formatVal(maxVal)}` : minVal ? `gt ${formatVal(minVal)}` : `lt ${formatVal(maxVal)}`;

    const popoverStyle = (() => {
        if (!popoverRef.current) return {};
        const rect = popoverRef.current.getBoundingClientRect();
        const dropdownWidth = 172;
        const left = rect.left + dropdownWidth > window.innerWidth ? rect.right - dropdownWidth : rect.left;

        return { top: rect.bottom + 4, left };
    })();

    return (
        <div className={`relative ${isDisabled ? 'opacity-40 pointer-events-none select-none' : ''}`} ref={popoverRef}>
            <ButtonCmp
                type="button"
                size="none"
                onClick={() => {
                    if (isOpen) commitPending();
                    setIsOpen(!isOpen);
                }}
                className={`!flex !items-center !gap-1.5 !text-xs !font-semibold !rounded-md !px-2.5 !h-[28px] !whitespace-nowrap !shadow-none ${
                    hasValue ? '!text-textSecondary !border !border-borderPrimary !bg-white' : '!text-textSecondary !border !border-dashed !border-borderPrimary hover:!bg-gray50'
                }`}
            >
                {hasValue ? (
                    <>
                        <span className="text-grayLight600">{label}:</span> {summary}
                        <XClose
                            className="w-4 h-4 cursor-pointer text-gray400"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                applyDebounce.cancel();
                                setMinVal('');
                                setMaxVal('');
                                pushValue(minName, '');
                                pushValue(maxName, '');
                                setIsOpen(false);
                            }}
                        />
                    </>
                ) : (
                    label
                )}
            </ButtonCmp>
            {isOpen && (
                <div className="fixed mt-1 bg-white border border-utilityGray200 rounded-lg shadow-lg p-3 z-[15] w-[172px]" style={popoverStyle}>
                    <div className="mb-3">
                        <label className="text-xs font-medium text-textSecondary block mb-1">{minPlaceholder}</label>
                        <InputText
                            inputName={minName}
                            size="sm"
                            placeholder={prefix ? '0.00' : '0'}
                            inputType="text"
                            shouldFormatNumber={true}
                            isAllowDecimal={true}
                            className={`form_control ${prefix ? 'pl-8' : ''}`}
                            parentClassName="mb-0"
                            value={minVal}
                            onChangeFunc={(e: any) => {
                                const val = e.target.value.replace(/,/g, '');
                                if (val !== '' && !/^\d*\.?\d{0,2}$/.test(val)) return;
                                setMinVal(val);
                                applyDebounce(val, maxVal);
                            }}
                            maxLength={10}
                            onBlur={commitPending}
                            icon={prefix ? <span className="absolute top-1/2 -translate-y-1/2 left-2.5 text-grayLight600 text-sm">{prefix}</span> : undefined}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-textSecondary block mb-1">{maxPlaceholder}</label>
                        <InputText
                            inputName={maxName}
                            size="sm"
                            placeholder={prefix ? '0.00' : '0'}
                            inputType="text"
                            shouldFormatNumber={true}
                            isAllowDecimal={true}
                            className={`form_control ${prefix ? 'pl-8' : ''}`}
                            parentClassName="mb-0"
                            value={maxVal}
                            maxLength={10}
                            isError={isMaxBelowMin}
                            errorText={isMaxBelowMin}
                            onChangeFunc={(e: any) => {
                                const val = e.target.value.replace(/,/g, '');
                                if (val !== '' && !/^\d*\.?\d{0,2}$/.test(val)) return;
                                setMaxVal(val);
                                applyDebounce(minVal, val);
                            }}
                            onBlur={commitPending}
                            icon={prefix ? <span className="absolute top-1/2 -translate-y-1/2 left-2.5 text-grayLight600 text-sm">{prefix}</span> : undefined}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RangePopover;
